import React from 'react';

import { ResponsiveImageProps } from './responsive-image.types';
import {
  generateMediaQueries,
  generateSourceSet,
} from './responsive-image.utils';

/**
 * A responsive image component that renders an image with multiple sources
 * for different screen sizes using the `<picture>` HTML element.
 *
 * This component allows you to define breakpoints for responsive images,
 * providing a flexible way to serve different image sources based on the
 * user's screen size. It also supports fallback sources, fetch priorities,
 * and lazy loading for improved performance.
 *
 * ### Example Usage
 * ```tsx
 * import { ResponsiveImage } from './ResponsiveImage';
 *
 * const breakpoints = [
 *   {
 *     media: '(max-width: 600px)',
 *     sources: [
 *       { src: 'image-small.jpg', descriptor: '1x' },
 *       { src: 'image-small@2x.jpg', descriptor: '2x' },
 *     ],
 *     sizes: '100vw',
 *   },
 *   {
 *     media: '(min-width: 601px)',
 *     sources: [
 *       { src: 'image-large.jpg', descriptor: '1x' },
 *       { src: 'image-large@2x.jpg', descriptor: '2x' },
 *     ],
 *     sizes: '50vw',
 *   },
 * ];
 *
 * <ResponsiveImage
 *   id="example-image"
 *   src="default-image.jpg"
 *   alt="An example responsive image"
 *   breakpoints={breakpoints}
 *   fallbackSrc="fallback-image.jpg"
 *   fetchPriority="high"
 *   loading="lazy"
 *   className="responsive-image"
 *   style={{ borderRadius: '8px' }}
 * />
 * ```
 *
 * ### Example HTML Output
 * ```html
 * <picture id="example-image" class="responsive-image" style="border-radius: 8px;">
 *   <source media="(max-width: 600px)" srcset="image-small.jpg 1x, image-small@2x.jpg 2x" sizes="100vw">
 *   <source media="(min-width: 601px)" srcset="image-large.jpg 1x, image-large@2x.jpg 2x" sizes="50vw">
 *   <img src="fallback-image.jpg" alt="An example responsive image" fetchpriority="high" loading="lazy" style="border-radius: 8px;">
 * </picture>
 * ```
 *
 * @component
 * @param {ResponsiveImageProps} props - The properties for the ResponsiveImage component.
 * @param {string} props.id - The unique identifier for the `<picture>` element.
 * @param {string} props.src - The default source URL for the image.
 * @param {string} props.alt - The alternative text for the image.
 * @param {Array} props.breakpoints - An array of breakpoint configurations for responsive images.
 * @param {string} [props.fallbackSrc] - The fallback source URL for the image if no breakpoints match.
 * @param {'auto' | 'high' | 'low'} [props.fetchPriority='auto'] - The fetch priority for the image.
 * @param {'lazy' | 'eager'} [props.loading='eager'] - The loading behavior for the image.
 * @param {string} [props.className] - Additional CSS class names for the `<picture>` element.
 * @param {React.CSSProperties} [props.style] - Inline styles for the `<picture>` element.
 *
 * @returns {JSX.Element} A responsive image component.
 */
export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  id,
  src,
  alt,
  breakpoints,
  fallbackSrc,
  fetchPriority = 'auto',
  loading = 'eager',
  className,
  style,
}) => {
  const mediaQueries = generateMediaQueries(breakpoints);
  const finalFallbackSource = fallbackSrc || src;

  return (
    <picture id={id} className={className} style={style}>
      {mediaQueries.map((query, index) => (
        <source
          key={index}
          media={query.media}
          srcSet={generateSourceSet(query.sources)}
          sizes={query.sizes || '100%'}
        />
      ))}
      <img
        src={finalFallbackSource}
        alt={alt}
        fetchPriority={fetchPriority}
        loading={loading}
        style={style}
      />
    </picture>
  );
};
